/* RECORDING AN ANCHOR. The one gap that mattered most.
 *
 * Six screens READ anchors. Nothing could ever write one, so the product's central
 * idea - that a member is known by a named person - was seed data only. On a real gym
 * the field would have stayed empty forever and the whole model would have degraded
 * into attendance tracking, which is the thing Acrostic argues against being.
 *
 * SITE-RULES 2 governs what may be written:
 *   an anchor is NAMED and TWO-WAY, never an assignment, so `basis` is REQUIRED: what
 *   actually makes it reciprocal, in the words of the person recording it. A support
 *   coach saying "they are mine" without saying what makes it two-way is exactly the
 *   assignment-wearing-an-anchor's-name that the rule exists to stop, which is why
 *   "I know them" fills the name in and still asks the question.
 *
 *   lastTwoWay starts TODAY, because recording one is itself a conversation.
 */
import { useState } from 'react';
import type { Member } from '@acrostic/model';
import { useAsync, useSource } from '../../data/SourceProvider.js';
import { Field, Modal } from '../../components/Modal.js';
import { useToast } from '../../components/Toast.js';

export function AnchorCapture({ m, prefill, onClose, onDone }: {
  m: Member;
  /** Set when the person recording it is the anchor. The basis is still asked for. */
  prefill?: { name: string; role: string } | undefined;
  onClose: () => void;
  onDone: () => void;
}) {
  const source = useSource();
  const toast = useToast();
  const staff = useAsync(() => source.getStaff(), [source]);
  const [name, setName] = useState(prefill?.name ?? '');
  const [role, setRole] = useState(prefill?.role ?? 'Coach');
  const [basis, setBasis] = useState('');
  const [busy, setBusy] = useState(false);

  async function save() {
    if (!name.trim()) { toast('An anchor is a named person', 'bad'); return; }
    if (!basis.trim()) {
      toast('Say what makes it two-way. Without that it is an assignment.', 'bad');
      return;
    }
    setBusy(true);
    try {
      const made = await source.addMemberAnchor(m.id, {
        name: name.trim(), kind: 'staff', role: role.trim(),
        basis: basis.trim(), since: new Date().toISOString().slice(0, 10),
      });
      /* makeAnchor in the model refuses an anchor with no basis, so the demo and the
         API refuse the same thing for the same reason. A null here is that refusal. */
      /* The model's own sentence, because it is the one that explains the refusal
         rather than announcing it. The server sends these words too. */
      if (!made) {
        toast('An anchor needs a basis: what makes it two-way, in your own words. '
          + 'Without one this is an assignment, not an anchor.', 'bad');
        setBusy(false);
        return;
      }
      toast(`${made.name} knows ${m.firstName}`, 'good');
      onDone();
    } catch (e) {
      toast((e as Error).message, 'bad');
      setBusy(false);
    }
  }

  return (
    <Modal
      title={`Who knows ${m.name}?`}
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn t-label-medium" onClick={onClose}>Cancel</button>
          <button type="button" className="btn primary t-label-medium" disabled={busy} onClick={() => void save()}>
            Record it
          </button>
        </>
      }
    >
      <p className="t-body-small muted">
      </p>

      <Field label="Who">
        <input
          className="field" list="anchor-candidates" value={name} autoFocus={!prefill}
          onChange={(e) => setName(e.target.value)}
          placeholder="The person who actually knows them"
        />
      </Field>
      <datalist id="anchor-candidates">
        {(staff.data ?? []).map((s) => <option key={s.id} value={s.name} />)}
      </datalist>

      <Field label="Their role here">
        <input className="field" value={role} onChange={(e) => setRole(e.target.value)} />
      </Field>

      <Field
        label="What makes it two-way"
        hint="In your own words. This is the field that makes it an anchor rather than a list."
      >
        <textarea
          className="field" rows={3} value={basis} autoFocus={!!prefill}
          placeholder="Spots them every Tuesday and they talk about their knee."
          onChange={(e) => setBasis(e.target.value)}
        />
      </Field>
    </Modal>
  );
}
